import { FC } from 'react'
import { useTheme } from 'styled-components'

import { AdsHighlightProps } from 'src/components/AdsHighlight'
import ColumnSection, {
  ColumnSectionProps,
} from 'src/components/blocks/BlockColumnSection'
import { ActionButtonProps } from '../../components/ActionButton'
import { AdsAuthorCardProps } from '../../components/AdsAuthorCard'
import { AdsDescriptionProps } from '../../components/AdsDescription'
import { AdsDetailsProps } from '../../components/AdsDetails'
import { AdsIntroProps } from '../../components/AdsIntro'
import AdsMediaGallery, {
  AdsMediaGalleryProps,
} from '../../components/AdsMediaGallery'
import AdsMediaSliderModal, {
  AdsMediaSliderModalProps,
} from '../../components/AdsMediaSliderModal'
import { AdsReplacementDateProps } from '../../components/AdsReplacementDate'
import { AdsShareSocialNetworksProps } from '../../components/AdsShareSocialNetworks'
import { AdsStatusProps } from '../../components/AdsStatus'
import { AdsTypesProps } from '../../components/AdsTypes'
import { BannerTitleTextButtonProps } from '../../components/BannerTitleTextButton'
import { BlockAdsRelatedProps } from '../../components/blocks/BlockAdsRelated'
import Icon, { Icons } from '../../components/Icon'
import { LinkProps } from '../../components/Link'
import SectionWrapper from '../../components/SectionWrapper'
import { MainLayoutProps } from '../../layouts'

import * as SC from './styled'

export type AdsTemplateProps = MainLayoutProps & {
  preview?: {
    status: AdsStatusProps
    modifyAdsButton?: ActionButtonProps
    publishAdsButton?: ActionButtonProps
  }
  backLink?: LinkProps
  adsIntro?: AdsIntroProps
  adsDescription?: AdsDescriptionProps
  adsAuthorCard?: AdsAuthorCardProps
  adsTypes?: AdsTypesProps
  adsHighlight?: AdsHighlightProps
  adsShareSocialNetwork?: AdsShareSocialNetworksProps
  adsReplacementDate?: AdsReplacementDateProps
  adsDetails?: AdsDetailsProps
  adsDetailsColumn?: AdsDetailsProps
  adsMediaGallery?: AdsMediaGalleryProps
  adsMediaSliderModal?: AdsMediaSliderModalProps
  answerAdButton?: ActionButtonProps
  bannerTitleTextButton?: BannerTitleTextButtonProps
  blockColumnSection?: ColumnSectionProps
  adsRelated?: BlockAdsRelatedProps
}

const AdsTemplate: FC<AdsTemplateProps> = ({
  preview,
  backLink,
  adsIntro,
  adsDescription,
  adsAuthorCard,
  adsTypes,
  adsHighlight,
  adsShareSocialNetwork,
  adsRelated,
  blockColumnSection,
  adsReplacementDate,
  adsDetailsColumn,
  adsDetails,
  adsMediaGallery,
  adsMediaSliderModal,
  answerAdButton,
  bannerTitleTextButton,

  ...layout
}) => {
  const theme = useTheme()

  return (
    <SC.Layout {...layout}>
      {preview && (
        <SC.PreviewWrapper>
          <SC.PreviewBlock>
            <SC.Status {...preview?.status} />
            <SC.PreviewButtons {...preview?.modifyAdsButton} />
          </SC.PreviewBlock>
          {preview?.publishAdsButton && (
            <SC.PreviewButtons {...preview?.publishAdsButton} />
          )}
        </SC.PreviewWrapper>
      )}
      <SectionWrapper spacing={'small'} maxWidth={'Xsmall'}>
        {backLink && (
          <SC.Backlink {...backLink}>
            <Icon
              icon={Icons.chevronLeft}
              color={theme.colors.bluePrimary}
              width={14}
              height={14}
            />
            {backLink.label}
          </SC.Backlink>
        )}
      </SectionWrapper>
      <SectionWrapper maxWidth={'Xsmall'} spacing="none">
        {adsIntro && <SC.Intro {...adsIntro} />}
      </SectionWrapper>
      <SectionWrapper maxWidth={'Xsmall'} spacing="large">
        <SC.Container>
          <SC.LeftWrapper>
            {adsDescription && <SC.Description {...adsDescription} />}
            {adsReplacementDate && (
              <SC.ReplacementDate {...adsReplacementDate} />
            )}
            {adsDetailsColumn && <SC.Details {...adsDetailsColumn} />}
            {adsDetails && <SC.Details {...adsDetails} />}
            {answerAdButton && <SC.AnswerAdButton {...answerAdButton} />}
          </SC.LeftWrapper>
          <SC.RightWrapper>
            {adsAuthorCard && <SC.AuthorCard {...adsAuthorCard} />}
            {adsHighlight && <SC.Highlight {...adsHighlight} />}
            {adsTypes && <SC.Types {...adsTypes} />}
            {adsShareSocialNetwork && (
              <SC.ShareSocialNetworks {...adsShareSocialNetwork} />
            )}
          </SC.RightWrapper>
        </SC.Container>
      </SectionWrapper>

      {adsMediaGallery && <AdsMediaGallery {...adsMediaGallery} />}
      {adsMediaSliderModal && <AdsMediaSliderModal {...adsMediaSliderModal} />}
      {bannerTitleTextButton && (
        <SC.TitleTextButtonBanner {...bannerTitleTextButton} />
      )}
      {blockColumnSection && (
        <SectionWrapper maxWidth={'Xsmall'}>
          <ColumnSection {...blockColumnSection} />
        </SectionWrapper>
      )}
      {adsRelated && (
        <SectionWrapper maxWidth={'Xsmall'}>
          <SC.AdsRelated {...adsRelated} />
        </SectionWrapper>
      )}
    </SC.Layout>
  )
}

export default AdsTemplate
